// Safe rich text for companion answers. The server's answer may carry a small
// markdown subset; anything else is shown as plain text. Output is a block
// list the renderer turns into <p>, <ul>, <strong> — never innerHTML.
//
// Also strips speech-only markup that must never reach the screen: SSML-ish
// tags (<break/>, <emphasis>), bracketed stage directions like [pause], and
// stray HTML.

const BREATH = /<\s*\/?\s*(break|emphasis|prosody|speak|voice|say-as|phoneme|sub|s|p)\b[^>]*>/gi;
// Stage directions in brackets, parentheses or asterisks: "[sigh]", "(laughs)", "*pauses*".
const STAGE = /(?:\[(?:pause|pauses|breath|breathes|laughs?|sighs?|chuckles?|inhales?|exhales?|clears throat)[^\]]*\]|\((?:pause|pauses|breath|breathes|laughs?|sighs?|chuckles?|inhales?|exhales?|clears throat)[^)]*\)|\*(?:pause|pauses|breath|breathes|laughs?|sighs?|chuckles?|inhales?|exhales?|clears throat)[^*]*\*)/gi;
const HTML_TAG = /<\/?[a-z][^>]*>/gi;

export function cleanSpeechMarkup(text) {
  // Keep blank lines: they are the paragraph breaks toBlocks relies on.
  return String(text ?? '').replace(BREATH, ' ').replace(STAGE, ' ').replace(HTML_TAG, '')
    .replace(/[ \t]+/g, ' ').replace(/[ \t]*\n[ \t]*/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

// Inline: only **bold** is honoured. Returns [{t:'text'|'bold', v}].
export function inlineRuns(line) {
  const runs = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0, m;
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) runs.push({ t: 'text', v: line.slice(last, m.index) });
    runs.push({ t: 'bold', v: m[1] });
    last = m.index + m[0].length;
  }
  if (last < line.length) runs.push({ t: 'text', v: line.slice(last) });
  return runs.map(r => ({ ...r, v: r.v.replace(/(^|[^*])\*(?!\*)/g, '$1') })); // lone asterisks are text
}

// Blocks: paragraphs and bullet lists. Headings (#) and numbered lists become
// plain paragraphs / bullets; nothing is dropped silently.
export function toBlocks(text) {
  const src = cleanSpeechMarkup(text);
  if (!src) return [];
  const lines = src.split('\n');
  const blocks = [];
  let para = [], list = null;
  const flushPara = () => { if (para.length) { blocks.push({ type: 'p', runs: inlineRuns(para.join(' ')) }); para = []; } };
  const flushList = () => { if (list) { blocks.push(list); list = null; } };
  for (const raw of lines) {
    const line = raw.replace(/^#{1,6}\s+/, '').trim();
    if (!line) { flushPara(); flushList(); continue; }
    const bullet = line.match(/^(?:[-*•]|\d+[.)])\s+(.*)$/);
    if (bullet) { flushPara(); if (!list) list = { type: 'ul', items: [] }; list.items.push(inlineRuns(bullet[1])); continue; }
    flushList(); para.push(line);
  }
  flushPara(); flushList();
  return blocks;
}

// Plain text for places that cannot render blocks (captions, aria labels).
export function toPlain(text) {
  return cleanSpeechMarkup(text).replace(/\*\*/g, '').replace(/^\s*(?:[-*•]|\d+[.)])\s+/gm, '').replace(/\n+/g, ' ').trim();
}
